import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import { useOptionalResponseArtifactEvents } from '../../../providers/ResponseArtifactEventProvider';
import {
  FlightArtifactBookingState,
  FlightPassengerPrefill,
  isSettledFlightBookingState,
  readFlightBookingState,
} from './flightArtifact';

export interface FlightArtifactSnapshot {
  flightBooking: FlightArtifactBookingState | null;
  flightPassengers: FlightPassengerPrefill[] | null;
}

interface ArtifactFetchResponse {
  success: boolean;
  artifact?: { userResponse?: unknown };
  flightPassengers?: FlightPassengerPrefill[];
}

const POLL_MS = 2500;
/** While parked on the saved-card payment (ops act within hours, not seconds). */
const PARKED_POLL_MS = 15 * 1000;
const POLL_CEILING_MS = 5 * 60 * 1000;

/**
 * Reads the flight artifact from the endpoint that fits where we are: the
 * public share endpoint on `/artifact/:token`, the authenticated one in-app.
 */
export function useFlightArtifactFetcher(
  artifactId: string,
  shareToken?: string,
): () => Promise<FlightArtifactSnapshot> {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  return useCallback(async () => {
    const response = shareToken
      ? await api.get<ArtifactFetchResponse>(
          `/public/artifacts/${encodeURIComponent(shareToken)}`,
        )
      : await api.get<ArtifactFetchResponse>(
          `/response-artifacts/artifact/${encodeURIComponent(artifactId)}`,
          isAuthenticated
            ? {
                headers: {
                  Authorization: `Bearer ${await getAccessTokenSilently()}`,
                },
              }
            : undefined,
        );
    const data = response.data;
    return {
      flightBooking: readFlightBookingState(data.artifact?.userResponse),
      flightPassengers: data.flightPassengers ?? null,
    };
  }, [artifactId, shareToken, getAccessTokenSilently, isAuthenticated]);
}

/**
 * Live `userResponse.flightBooking` for an artifact while a Book press is in
 * flight: Pusher `RESPONSE_ARTIFACT_UPDATED` when a channel is available
 * (in-app), polling otherwise (public share page) and as a safety net.
 * Stops once the state settles.
 */
export function useFlightBookingState(
  artifactId: string,
  active: boolean,
  fetchSnapshot: () => Promise<FlightArtifactSnapshot>,
): FlightArtifactBookingState | null {
  const [state, setState] = useState<FlightArtifactBookingState | null>(null);
  const events = useOptionalResponseArtifactEvents();
  const settledRef = useRef(false);
  const parkedRef = useRef(false);

  // Each activation is a new attempt: start clean. The last settled result
  // is kept after deactivation so the view keeps showing it until the host
  // hands down a refreshed artifact.
  useEffect(() => {
    if (active) {
      settledRef.current = false;
      parkedRef.current = false;
      setState(null);
    }
  }, [active]);

  const apply = useCallback((next: FlightArtifactBookingState | null) => {
    if (!next || settledRef.current) return;
    if (isSettledFlightBookingState(next)) settledRef.current = true;
    parkedRef.current = next.state === 'awaiting_payment';
    setState(next);
  }, []);

  useEffect(() => {
    if (!active || !events) return;
    const callbackId = `flight-booking-${artifactId}`;
    events.registerUpdatedCallback(
      callbackId,
      (data: { artifactId?: string; userResponse?: unknown }) => {
        if (data?.artifactId !== artifactId) return;
        apply(readFlightBookingState(data.userResponse));
      },
    );
    return () => events.unregisterUpdatedCallback(callbackId);
  }, [active, events, artifactId, apply]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let startedAt = Date.now();
    let wasParked = false;
    const tick = async () => {
      if (cancelled || settledRef.current) return;
      if (parkedRef.current && !wasParked) {
        // Parking is a fresh wait, not the tail of the Book press.
        wasParked = true;
        startedAt = Date.now();
      }
      if (Date.now() - startedAt > POLL_CEILING_MS) return;
      try {
        const snapshot = await fetchSnapshot();
        if (!cancelled) apply(snapshot.flightBooking);
      } catch {
        // transient; the next tick retries
      }
      if (!cancelled && !settledRef.current) {
        timer = window.setTimeout(
          tick,
          parkedRef.current ? PARKED_POLL_MS : POLL_MS,
        );
      }
    };
    let timer = window.setTimeout(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [active, fetchSnapshot, apply]);

  return state;
}
